import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Spin, Button, Modal, Checkbox, message } from 'antd';
import { 
  UserOutlined, 
  CheckCircleOutlined, 
  TeamOutlined,
  TrophyOutlined,
  DownloadOutlined 
} from '@ant-design/icons';
import { getStatistics, exportLearningData } from '../../api/admin';
import { utils as XLSXUtils, writeFile as XLSXWriteFile } from 'xlsx';

const Statistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedFields, setSelectedFields] = useState([
    'employee_id', 'name', 'department', 'step1', 'step2', 'step3', 'step4', 'step5', 'step6', 'score', 'promise', 'status'
  ]);

  const fieldOptions = [
    { label: '工号', value: 'employee_id' },
    { label: '姓名', value: 'name' },
    { label: '部门', value: 'department' },
    { label: '步骤1-跟读', value: 'step1' },
    { label: '步骤2-播客', value: 'step2' },
    { label: '步骤3-完形填空', value: 'step3' },
    { label: '步骤4-评分', value: 'step4' },
    { label: '步骤5-找茬', value: 'step5' },
    { label: '步骤6-承诺', value: 'step6' },
    { label: '评分分数', value: 'score' },
    { label: '承诺内容', value: 'promise' },
    { label: '整体状态', value: 'status' },
  ];

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      const response = await getStatistics();
      if (response.code === 200) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('加载统计数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      message.warning('请至少选择一个导出字段');
      return;
    }

    try {
      setExporting(true);
      const fields = selectedFields.join(',');
      const response = await exportLearningData(fields);
      
      if (response.code === 200 && response.data) {
        // 创建工作簿
        const ws = XLSXUtils.json_to_sheet(response.data);
        const wb = XLSXUtils.book_new();
        XLSXUtils.book_append_sheet(wb, ws, '学习数据');
        
        // 生成文件名
        const now = new Date();
        const filename = `学习数据导出_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.xlsx`;
        
        // 下载文件
        XLSXWriteFile(wb, filename);
        message.success('导出成功');
        setExportModalVisible(false);
      }
    } catch (error) {
      message.error('导出失败: ' + (error.message || '未知错误'));
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const departmentColumns = [
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department'
    },
    {
      title: '总人数',
      dataIndex: 'total_count',
      key: 'total_count'
    },
    {
      title: '完成人数',
      dataIndex: 'completed_count',
      key: 'completed_count'
    },
    {
      title: '完成率',
      key: 'completion_rate',
      render: (_, record) => {
        const rate = record.total_count > 0 
          ? Math.round((record.completed_count / record.total_count) * 100)
          : 0;
        return `${rate}%`;
      }
    }
  ];

  const weakItemColumns = [
    {
      title: '准则序号',
      dataIndex: 'weak_item',
      key: 'weak_item'
    },
    {
      title: '被选次数',
      dataIndex: 'count',
      key: 'count'
    },
    {
      title: '占比',
      key: 'percentage',
      render: (_, record) => {
        const total = stats?.weak_item_stats?.reduce((sum, item) => sum + item.count, 0) || 1;
        return `${Math.round((record.count / total) * 100)}%`;
      }
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0 }}>数据统计</h2>
        <Button 
          type="primary" 
          icon={<DownloadOutlined />} 
          onClick={() => setExportModalVisible(true)}
        >
          导出数据
        </Button>
      </div>
      
      {/* 核心指标 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总人数"
              value={stats?.total_users || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已完成人数"
              value={stats?.completed_users || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="完成率"
              value={stats?.completion_rate || 0}
              suffix="%"
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="平均分"
              value={stats?.avg_avg_score || 0}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 部门统计 */}
      <Card title="部门完成情况" style={{ marginBottom: 24 }}>
        <Table
          columns={departmentColumns}
          dataSource={stats?.department_stats || []}
          rowKey="department"
          pagination={false}
        />
      </Card>

      {/* 薄弱项统计 */}
      <Card title="薄弱项分布">
        <Table
          columns={weakItemColumns}
          dataSource={stats?.weak_item_stats || []}
          rowKey="weak_item"
          pagination={false}
        />
      </Card>

      {/* 导出数据弹窗 */}
      <Modal
        title="导出学习数据"
        open={exportModalVisible}
        onOk={handleExport}
        onCancel={() => setExportModalVisible(false)}
        confirmLoading={exporting}
        okText="导出"
        cancelText="取消"
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ marginBottom: 8 }}>请选择要导出的字段：</p>
          <Checkbox.Group
            options={fieldOptions}
            value={selectedFields}
            onChange={(values) => setSelectedFields(values)}
            style={{ width: '100%' }}
          >
            <Row>
              {fieldOptions.map(opt => (
                <Col span={12} key={opt.value} style={{ marginBottom: 8 }}>
                  <Checkbox value={opt.value}>{opt.label}</Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </div>
        <div style={{ color: '#999', fontSize: 12 }}>
          提示：选择需要导出的字段后点击"导出"按钮，将生成Excel文件下载。
        </div>
      </Modal>
    </div>
  );
};

export default Statistics;
